/**
 * Resolves and renders artifacts by name.
 *
 * Takes a list of artifact names and fetches their content via the
 * GET /artifacts/resolve endpoint. Used for both streaming (artifact
 * names arrive via CustomEvent) and history (names from message metadata).
 */
import { type FC, useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { BlobFileMessage } from "~/components/assistant/messages/BlobFileMessage";
import { DataValueMessage } from "~/components/assistant/messages/DataValueMessage";
import { DataframeMessage } from "~/components/assistant/messages/DataframeMessage";
import { FigureMessage } from "~/components/assistant/messages/FigureMessage";
import { TextFileMessage } from "~/components/assistant/messages/TextFileMessage";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface EnrichedArtifact {
  artifact_id: string;
  artifact_name?: string;
  type: "figure" | "dataframe" | "value" | "text" | "blob";
  content: any;
  summary?: string;
  code?: string;
}

const ARTIFACT_COMPONENTS: Record<
  EnrichedArtifact["type"],
  { Component: FC<any>; propName: string }
> = {
  figure: { Component: FigureMessage, propName: "figure" },
  dataframe: { Component: DataframeMessage, propName: "dataframe" },
  value: { Component: DataValueMessage, propName: "value" },
  text: { Component: TextFileMessage, propName: "textFile" },
  blob: { Component: BlobFileMessage, propName: "blobFile" },
};

const ArtifactRenderer: FC<{ artifact: EnrichedArtifact }> = ({ artifact }) => {
  const entry = ARTIFACT_COMPONENTS[artifact.type];
  if (!entry) {
    return null;
  }
  const { Component, propName } = entry;
  const data = {
    artifact_id: artifact.artifact_id,
    artifact_name: artifact.artifact_name,
    content: artifact.content,
    summary: artifact.summary,
    code: artifact.code,
  };
  return (
    <div className="px-2">
      <Component {...{ [propName]: data }} debugMode={false} />
    </div>
  );
};

export const ArtifactDisplay: FC<{ artifactNames: string[] }> = ({ artifactNames }) => {
  const artifactKey = artifactNames.join(",");
  const [enrichedArtifacts, setEnrichedArtifacts] = useState<EnrichedArtifact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedRef = useRef<string>("");
  const { conversationId } = useAssistantContext();

  useEffect(() => {
    if (!artifactKey || !conversationId) {
      return;
    }
    if (fetchedRef.current === artifactKey) {
      return;
    }
    fetchedRef.current = artifactKey;

    let cancelled = false;

    const resolve = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        for (const name of artifactNames) {
          params.append("names", name);
        }

        const response = await fetch(
          `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/artifacts/resolve?${params}`
        );

        if (!response.ok) {
          throw new Error(`Failed to resolve artifacts: ${response.status}`);
        }

        const resolved: EnrichedArtifact[] = await response.json();
        if (!cancelled) {
          setEnrichedArtifacts(resolved);
        }
      } catch (error) {
        console.error("Error resolving artifacts:", error);
        fetchedRef.current = "";
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    resolve();

    return () => {
      cancelled = true;
      fetchedRef.current = "";
    };
  }, [artifactKey, conversationId, artifactNames]);

  const showLoading = isLoading && enrichedArtifacts.length === 0;

  if (enrichedArtifacts.length === 0 && !showLoading) {
    return null;
  }

  return (
    <div className="w-full space-y-4 my-4">
      {showLoading && (
        <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span>Loading artifacts…</span>
        </div>
      )}

      {enrichedArtifacts.map((artifact, index) => (
        <ArtifactRenderer key={artifact.artifact_id || `artifact-${index}`} artifact={artifact} />
      ))}
    </div>
  );
};
